<script>
  import { anonymizationFieldError as fieldError } from "lib/util/regex.js";
  import { ComboBox, DataTable} from "carbon-components-svelte";
  import { Pagination, TextArea, TextInput, Button } from "paxray-components-svelte";
  import { PLUS_ICON } from "@utils/icons.js";
  import Modal from "lib/components/Modal.svelte";
  import CopyIcon from "lib/components/common/icons/app/CopyIcon.svelte";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../settings/svg/RemoveIcon.svelte";

  export let kind = "windowtitles";
  export let rules = [];
  // Read only rules can be filtered and searched but not changed.
  export let readonly = false;
  // Hides the application column and filter for rules that apply to all apps.
  export let hideApplication = false;
  // Optional { save(rule), remove(rule) } that store changes in the backend.
  // save resolves to the stored rule. Without it, edits stay in-memory drafts.
  export let persist = null;

  const windowFields = [
    { key: "search", label: "Search pattern" },
    { key: "replace", label: "Replacement" },
  ];
  const elementFields = [
    { key: "namesearch", label: "Name search pattern" },
    { key: "namereplace", label: "Name replacement" },
    { key: "automationsearch", label: "Automation ID search pattern" },
    { key: "automationreplace", label: "Automation ID replacement" },
  ];
  let search = "";
  let applicationFilter = null;
  let classFilter = null;
  let typeFilter = null;
  let page = 1;
  let pageSize = 10;
  let editorOpen = false;
  let deleteOpen = false;
  let deleteRule = null;
  let form = {};
  let saving = false;
  let errorOpen = false;
  let errorMessage = "";

  $: fields = kind === "windowtitles" ? windowFields : elementFields;
  $: applicationOptions = scopeOptions(
    rules,
    "applicationname",
    "All-app rules (*)",
  );
  $: classOptions = scopeOptions(rules, "elementclass", "All-class rules (*)");
  $: typeOptions = scopeOptions(rules, "type", "All-type rules (*)");
  $: headers = [
    { key: "name", value: "Rule" },
    ...(hideApplication ? [] : [{ key: "applicationname", value: "Application" }]),
    ...(kind === "elements"
      ? [
          { key: "elementclass", value: "Class" },
          { key: "type", value: "Type" },
          { key: "nameRule", value: "Name" },
          { key: "automationRule", value: "Automation ID" },
        ]
      : fields.map(({ key, label }) => ({ key, value: label }))),
    ...(readonly ? [] : [{ key: "actions", value: "Actions" }]),
  ];
  // Same natural order as name_order in the backend, so the table shows the execution order.
  function nameOrder(name) {
    return String(name ?? "")
      .split(/(\d+)/)
      .map((part) => (/^\d+$/.test(part) ? [0, Number(part)] : [1, part.toLowerCase()]));
  }

  function compareNames(a, b) {
    const left = nameOrder(a);
    const right = nameOrder(b);
    for (let index = 0; index < Math.min(left.length, right.length); index++) {
      for (const slot of [0, 1]) {
        if (left[index][slot] < right[index][slot]) return -1;
        if (left[index][slot] > right[index][slot]) return 1;
      }
    }
    return left.length - right.length;
  }

  $: rows = rules
    .filter(
      (rule) =>
        (!applicationFilter ||
          scopeValue(rule.applicationname) === applicationFilter) &&
        (kind !== "elements" ||
          ((!classFilter || scopeValue(rule.elementclass) === classFilter) &&
            (!typeFilter || scopeValue(rule.type) === typeFilter))),
    )
    .filter((rule) =>
      Object.values(rule).some((value) =>
        String(value).toLowerCase().includes(search.trim().toLowerCase()),
      ),
    )
    .slice()
    // Numerischer Vergleich, damit Ausführungsgruppe 10 nach 2 einsortiert wird.
    .sort((a, b) => compareNames(a.name, b.name));
  $: if (page > Math.max(1, Math.ceil(rows.length / pageSize))) page = 1;
  $: kind, resetFilters();
  $: search, applicationFilter, classFilter, typeFilter, (page = 1);
  $: duplicateName = rules.some(
    (rule) => rule.id !== form.id && rule.name === (form.name || "").trim(),
  );
  $: fieldErrors = Object.fromEntries(
    fields
      .map(({ key }) => [key, fieldError(key, form)])
      .filter(([, error]) => error),
  );
  $: saveDisabled =
    saving ||
    !form.name?.trim() ||
    duplicateName ||
    Object.keys(fieldErrors).length > 0 ||
    (kind === "windowtitles"
      ? !form.search?.trim()
      : !form.namesearch?.trim() && !form.automationsearch?.trim());

  function scopeValue(value) {
    return (value || "").trim() || "*";
  }

  function scopeOptions(rules, field, wildcardLabel) {
    return [...new Set(rules.map((rule) => scopeValue(rule[field])))]
      .sort()
      .map((value) => ({
        id: value,
        text: value === "*" ? wildcardLabel : value,
      }));
  }

  function shouldFilterItem(item, value) {
    return item.text.toLowerCase().includes(value.toLowerCase());
  }

  function resetFilters() {
    applicationFilter = null;
    classFilter = null;
    typeFilter = null;
    search = "";
  }

  function openEditor(rule = null, copy = false) {
    form = rule
      ? { ...rule }
      : {
          name: "",
          applicationname: kind === "elements" ? "*" : "",
          search: "",
          replace: "",
          namesearch: "",
          namereplace: "",
          automationsearch: "",
          automationreplace: "",
          elementclass: "*",
          type: "*",
        };
    if (copy) {
      delete form.id;
      form.name = `${form.name} - Copy`;
    }
    editorOpen = true;
  }

  async function saveDraft() {
    if (saveDisabled) return;
    let rule = {
      ...form,
      name: form.name.trim(),
      id: form.id || crypto.randomUUID(),
    };
    if (persist) {
      saving = true;
      try {
        const saved = await persist.save({ ...rule, id: form.id || null });
        if (!saved) return;
        rule = saved;
      } catch (error) {
        errorMessage = error.details?.join("; ") || error.message;
        errorOpen = true;
        return;
      } finally {
        saving = false;
      }
    }
    rules = form.id
      ? rules.map((item) => (item.id === form.id ? rule : item))
      : [...rules, rule];
    editorOpen = false;
  }

  async function removeDraft() {
    if (!deleteRule) return;
    if (saving) return;
    if (persist) {
      saving = true;
      try {
        if (!(await persist.remove(deleteRule))) return;
      } catch (error) {
        errorMessage = error.details?.join("; ") || error.message;
        errorOpen = true;
        return;
      } finally {
        saving = false;
      }
    }
    rules = rules.filter((rule) => rule.id !== deleteRule.id);
    deleteOpen = false;
    deleteRule = null;
  }
</script>

<section class="global-anon">
  {#if $$slots.notices}
    <!-- Read only tabs pass their own developer notices. -->
    <div class="developer-notices">
      <slot name="notices" />
    </div>
  {/if}
  <div class="toolbar">
    {#if !hideApplication}
      <div class="scope-filter">
        <ComboBox
          id="global-anon-application-filter"
          titleText="Application"
          placeholder="All applications"
          items={applicationOptions}
          bind:selectedId={applicationFilter}
          {shouldFilterItem}
        />
      </div>
    {/if}
    {#if kind === "elements"}
      <div class="scope-filter">
        <ComboBox
          id="global-anon-class-filter"
          titleText="Class"
          placeholder="All classes"
          items={classOptions}
          bind:selectedId={classFilter}
          {shouldFilterItem}
        />
      </div>
      <div class="scope-filter">
        <ComboBox
          id="global-anon-type-filter"
          titleText="Type"
          placeholder="All types"
          items={typeOptions}
          bind:selectedId={typeFilter}
          {shouldFilterItem}
        />
      </div>
    {/if}
    <div class="search">
      <TextInput
        id="global-anon-search"
        type="search"
        size="medium"
        labelText="Search rules"
        bind:value={search}
      />
    </div>
    <button
      type="button"
      class="reset-filter-button"
      title="Reset filters"
      aria-label="Reset filters"
      on:click={resetFilters}
      disabled={!search && !applicationFilter && !classFilter && !typeFilter}
    >
      <img src="/img/assistant/icons/reset.svg" alt="" />
    </button>
    {#if !readonly}
      <Button
        text="Add rule"
        kind="secondary"
        size="field"
        iconLeft
        icon={PLUS_ICON}
        on:click={() => openEditor()}
      />
    {/if}
  </div>
  <div class="table-scroll">
    <DataTable
      zebra
      class="custom-table zebra-rows"
      {headers}
      {rows}
      {page}
      {pageSize}
      size="short"
    >
      <svelte:fragment slot="cell" let:cell let:row>
        {#if cell.key === "actions"}
          <div class="row-actions">
            <button
              type="button"
              class="table-action-button"
              title="Edit"
              aria-label="Edit"
              on:click={() => openEditor(row)}><EditIcon /></button
            >
            <button
              type="button"
              class="table-action-button"
              title="Duplicate"
              aria-label="Duplicate"
              on:click={() => openEditor(row, true)}><CopyIcon /></button
            >
            <button
              type="button"
              class="table-action-button danger"
              title="Delete"
              aria-label="Delete"
              on:click={() => {
                deleteRule = row;
                deleteOpen = true;
              }}><RemoveIcon /></button
            >
          </div>
        {:else if cell.key === "nameRule"}
          <div class="rule-cell">
            {#if row.namesearch}
              <div class="rule-line">
                <span>Find</span><code>{row.namesearch}</code>
              </div>
              <div class="rule-line">
                <span>Replace</span><code>{row.namereplace || "(empty)"}</code>
              </div>
            {:else}
              <span>—</span>
            {/if}
          </div>
        {:else if cell.key === "automationRule"}
          <div class="rule-cell">
            {#if row.automationsearch}
              <div class="rule-line">
                <span>Find</span><code>{row.automationsearch}</code>
              </div>
              <div class="rule-line">
                <span>Replace</span><code
                  >{row.automationreplace || "(empty)"}</code
                >
              </div>
            {:else}
              <span>—</span>
            {/if}
          </div>
        {:else if ["applicationname", "elementclass", "type"].includes(cell.key) && scopeValue(cell.value) === "*"}
          <span class="scope-all">All</span>
        {:else}
          <span class="cell-value">{cell.value || "—"}</span>
        {/if}
      </svelte:fragment>
    </DataTable>
    {#if rows.length === 0}
      <p class="empty-state">
        {rules.length
          ? "No rules match your search."
          : "No rules yet. Add a rule to try this editor."}
      </p>
    {/if}
    <div class="table-pagination">
      <Pagination
        dark
        bind:page
        bind:pageSize
        totalItems={rows.length}
        pageSizes={[10, 25, 50]}
      />
    </div>
  </div>
</section>

<Modal
  bind:open={editorOpen}
  modalHeading={`${form.id ? "Edit" : "Add"} ${kind === "windowtitles" ? "app or document title" : "button, field or label"} rule`}
  actions
  autowidth
  primaryButtonText={persist ? "Save rule" : "Save draft"}
  primaryButtonVariant="secondary"
  primaryButtonValidations={saveDisabled}
  primaryButtonAction={saveDraft}
>
  <div class="editor">
    <TextInput
      id="global-anon-name"
      labelText="Rule"
      bind:value={form.name}
      helperText="Use an English name starting with the execution group, e.g. 2 - All apps - Cleanup - Remove Chrome suffix."
      invalid={duplicateName}
      invalidText="A rule with this name already exists."
    />
    <TextInput
      id="global-anon-app"
      labelText="Application"
      bind:value={form.applicationname}
      helperText={kind === "windowtitles"
        ? "Leave empty to match all applications."
        : "Use * to match all applications."}
    />
    {#if kind === "elements"}
      <div class="field-grid">
        <TextInput
          id="global-anon-class"
          labelText="Class"
          bind:value={form.elementclass}
          helperText="Use * for all classes."
        />
        <TextInput
          id="global-anon-type"
          labelText="Type"
          bind:value={form.type}
          helperText="Use * for all types."
        />
      </div>
    {/if}
    <div class="field-grid">
      {#each kind === "windowtitles" ? windowFields : elementFields as field}
        <div class="pattern-field">
          <TextArea
            id={`global-anon-field-${field.key}`}
            labelText={field.label}
            rows={3}
            bind:value={form[field.key]}
            invalid={!!fieldErrors[field.key]}
            on:keydown={(event) => {
              if (event.key === "Enter") event.stopPropagation();
            }}
          />
          <!-- Eigener Hinweis, da TextArea seinen invalidText ausblendet. -->
          {#if fieldErrors[field.key]}
            <p class="regex-error">{fieldErrors[field.key]}</p>
          {/if}
        </div>
      {/each}
    </div>
    <p>
      Use markers such as <code>&lt;&lt;&lt;TEXT&gt;&gt;&gt;</code> for masked
      content or <code>$1</code> to retain a capture group. An empty replacement
      removes the match.
    </p>
    {#if !persist}
      <p>
        Search patterns are not executed or checked by the backend in this
        preview.
      </p>
    {/if}
  </div>
</Modal>

<Modal
  bind:open={deleteOpen}
  modalHeading={persist ? "Delete rule?" : "Delete draft rule?"}
  actions
  autowidth
  primaryButtonValidations={saving}
  primaryButtonText={persist ? "Delete rule" : "Delete draft"}
  primaryButtonVariant="secondary"
  primaryButtonAction={removeDraft}
>
  <p>
    {persist
      ? `Delete “${deleteRule?.name || ""}”? This takes effect in the next pipeline run.`
      : `Delete “${deleteRule?.name || ""}” from this draft?`}
  </p>
</Modal>

<Modal bind:open={errorOpen} modalHeading="Unable to save rule" actions autowidth noSecondaryButton
  primaryButtonText="Close" primaryButtonVariant="secondary" primaryButtonAction={() => (errorOpen = false)}>
  <p>{errorMessage}</p>
</Modal>

<style>
  .global-anon,
  .editor {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    min-width: 0;
  }
  p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
  .developer-notices {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--control-gap);
    align-items: stretch;
  }
  .developer-notices :global(.warning-notice) {
    margin-bottom: 0;
  }
  .developer-notices :global(.developer-notice) {
    line-height: 1.35;
  }
  .developer-notices :global(.developer-notice p),
  .developer-notices :global(.developer-notice ul),
  .developer-notices :global(.developer-notice ol) {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.35;
  }
  .developer-notices :global(.developer-notice ul),
  .developer-notices :global(.developer-notice ol) {
    padding-left: 1.1rem;
  }
  .developer-notices :global(.developer-notice ul) {
    list-style: disc;
  }
  .developer-notices :global(.developer-notice ol) {
    list-style: decimal;
  }
  .developer-notices :global(.developer-notice li) {
    display: list-item;
    margin-top: 0.125rem;
  }
  .developer-notices :global(.developer-notice code) {
    overflow-wrap: anywhere;
    font-size: 0.75rem;
  }
  .toolbar {
    display: flex;
    align-items: flex-end;
    gap: var(--control-gap);
    flex-wrap: wrap;
  }
  .search {
    flex: 1;
    min-width: 12rem;
  }
  .scope-filter {
    flex: 1 1 12rem;
    min-width: 12rem;
  }
  /* The input already draws the border, so the list box around it needs none. */
  .scope-filter :global(.bx--combo-box.bx--list-box) {
    border: 0 !important;
    background: transparent;
  }
  .reset-filter-button {
    display: flex;
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    cursor: pointer;
  }
  .reset-filter-button img {
    width: 1rem;
    height: 1rem;
  }
  .reset-filter-button:hover:not(:disabled) {
    background: var(--pax-orange);
  }
  .reset-filter-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }
  .row-actions {
    display: grid;
    grid-template-columns: repeat(3, 2rem);
    justify-content: center;
    gap: var(--control-gap);
  }
  .table-action-button {
    box-sizing: border-box;
    display: grid;
    width: 2rem;
    height: 2rem;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 0.35rem;
    background: var(--pax-black);
    cursor: pointer;
  }
  .table-action-button.danger:hover {
    background: var(--pax-danger);
  }
  .table-action-button:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .cell-value {
    display: block;
    max-width: 28rem;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  .scope-all {
    color: #8d8d8d;
  }
  .rule-cell {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    max-width: 22rem;
    overflow-wrap: anywhere;
  }
  .rule-line {
    display: grid;
    grid-template-columns: 3.5rem minmax(0, 1fr);
    gap: 0.4rem;
    align-items: start;
  }
  .rule-line > span {
    color: var(--pax-sub);
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.35;
    text-transform: uppercase;
  }
  .rule-line code {
    font-family: inherit;
    font-size: inherit;
    line-height: 1.35;
    white-space: pre-wrap;
  }
  .table-pagination {
    overflow: hidden;
  }
  .table-pagination :global(.bx--pagination) {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.5rem;
    border: 0;
    border-top: 1px solid var(--pax-sub-new);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__text) {
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--select-input) {
    border-color: var(--pax-sub-new) !important;
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--select-input:hover),
  .table-pagination :global(.bx--select-input:focus) {
    background: var(--pax-sub-new) !important;
  }
  .table-pagination :global(.bx--select__arrow) {
    fill: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button) {
    border-left-color: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button .bx--btn__icon),
  .table-pagination :global(.bx--pagination__button .bx--btn__icon path) {
    fill: currentColor !important;
  }
  .table-pagination :global(.bx--pagination__button:hover) {
    background: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button:disabled) {
    background: var(--pax-black);
    color: var(--pax-grey);
  }
  .table-scroll :global(.bx--data-table-container) {
    min-width: 64rem;
  }
  .table-scroll :global(.bx--data-table) {
    width: 100%;
    border-right: 1px solid var(--border-color);
    border-left: 1px solid var(--border-color);
    font-size: 0.8125rem;
  }
  .table-scroll :global(.bx--data-table th),
  .table-scroll :global(.bx--data-table td) {
    border-right: 0;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-black);
    text-align: left;
    vertical-align: middle;
  }
  .table-scroll :global(.bx--data-table th) {
    height: 2.5rem;
    padding: 0.5rem 1rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1;
  }
  .table-scroll :global(.bx--data-table td) {
    padding: 0.75rem 1rem;
  }
  .table-scroll :global(.bx--data-table th:first-child) {
    border-radius: 0.5rem 0 0 0 !important;
  }
  .table-scroll :global(.bx--data-table th:last-child) {
    border-radius: 0 0.5rem 0 0 !important;
  }
  .table-scroll :global(.bx--data-table th .bx--table-header-label) {
    color: var(--pax-white);
  }
  .table-scroll
    :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td
    ) {
    background-color: #ffffff !important;
  }
  .table-scroll
    :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }
  .table-scroll :global(.bx--data-table tbody tr:hover td) {
    background: var(--pax-smoke) !important;
  }
  .table-scroll :global(.bx--data-table th:last-child),
  .table-scroll :global(.bx--data-table td:last-child) {
    width: 8.5rem;
    min-width: 8.5rem;
    padding: 0.5rem;
    border-right: 0;
    text-align: center;
    white-space: nowrap;
  }
  .table-scroll :global(.bx--data-table th:last-child .bx--table-header-label) {
    display: flex;
    justify-content: center;
  }
  .empty-state {
    padding: 1.5rem;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-sub);
    text-align: center;
  }
  .pattern-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }
  .regex-error {
    color: var(--pax-danger);
    font-size: 0.75rem;
    font-weight: 700;
  }
  .field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--control-gap);
  }
  @media (max-width: 640px) {
    .developer-notices,
    .field-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
